import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Building2, Printer, FileSpreadsheet, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { downloadHospitalClaimsCSV, printHospitalClaims, downloadHospitalPricelist, printHospitalPricelist } from './exportUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PER_PAGE = 6;

const currentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
};

export default function HospitalMonthlyClaimsTable({ allHospitals, hospitalStats, authCfg }) {
  const [page, setPage] = useState(0);
  const thisMonth = currentMonthKey();
  const [month, setMonth] = useState(thisMonth);
  const [pastStats, setPastStats] = useState(null);
  const isCurrentMonth = month === thisMonth;

  useEffect(() => {
    setPage(0);
    if (isCurrentMonth) {
      setPastStats(null);
      return;
    }
    let cancelled = false;
    axios.get(`${API}/claims/hospital-stats`, { ...authCfg, params: { month } })
      .then((res) => { if (!cancelled) setPastStats(res.data); })
      .catch(() => { if (!cancelled) setPastStats({}); });
    return () => { cancelled = true; };
  }, [month]);

  const stats = isCurrentMonth ? hospitalStats : (pastStats || {});

  const hospitalsWithClaims = allHospitals.map((h) => {
    const s = stats[h.hospital_name] || {};
    const totalAmount = (s.total_pending || 0) + (s.total_paid || 0);
    const claimCount = (s.pending_count || 0) + (s.paid_count || 0);
    return {
      name: h.hospital_name,
      location: h.address || 'N/A',
      total: totalAmount,
      count: claimCount,
      avg: claimCount > 0 ? totalAmount / claimCount : 0,
    };
  }).sort((a, b) => b.total - a.total);

  const totalPages = Math.ceil(hospitalsWithClaims.length / PER_PAGE);
  const startIdx = page * PER_PAGE;
  const paginatedHospitals = hospitalsWithClaims.slice(startIdx, startIdx + PER_PAGE);

  const totalClaims = Object.values(stats).reduce((sum, s) => sum + (s.pending_count || 0) + (s.paid_count || 0), 0);
  const totalAmount = Object.values(stats).reduce((sum, s) => sum + (s.total_pending || 0) + (s.total_paid || 0), 0);
  const avgClaim = totalClaims > 0 ? totalAmount / totalClaims : 0;
  // Average-claim colour bands: < $40 green, < $50 yellow, < $70 orange, $70+ red
  const avgTone = (avg) => (avg <= 0 ? 'text-gray-400' : avg < 40 ? 'text-green-600' : avg < 50 ? 'text-yellow-500' : avg < 70 ? 'text-orange-500' : 'text-red-600');

  return (
    <Card className="border-emerald-200 shadow-md">
      <CardHeader className="bg-gradient-to-r from-emerald-50 to-emerald-100 border-b border-emerald-200 p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-emerald-900 text-sm sm:text-base">
            <Building2 className="w-4 h-4 sm:w-5 sm:h-5" />
            {isCurrentMonth ? 'Hospital Claims - This Month' : `Hospital Claims - ${monthLabel(month)}`}
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 bg-white border border-emerald-200 rounded-md px-2 py-1">
              <Calendar className="w-4 h-4 text-emerald-700" />
              <input
                type="month"
                value={month}
                max={thisMonth}
                onChange={(e) => setMonth(e.target.value || thisMonth)}
                className="text-xs sm:text-sm text-gray-700 bg-transparent outline-none cursor-pointer"
                data-testid="hospital-monthly-table-month-select"
              />
            </div>
            <Button
              onClick={() => printHospitalClaims(allHospitals, stats, isCurrentMonth ? null : month)}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-xs sm:text-sm"
            >
              <Printer className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Print
            </Button>
            <Button
              onClick={() => downloadHospitalClaimsCSV(allHospitals, stats, isCurrentMonth ? null : month)}
              variant="outline"
              size="sm"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs sm:text-sm"
            >
              Download CSV
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-3 sm:p-4">
        <div className="overflow-x-auto -mx-2 sm:mx-0">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-2 sm:p-3 font-semibold text-gray-700">#</th>
                <th className="text-left p-2 sm:p-3 font-semibold text-gray-700">Hospital</th>
                <th className="text-left p-2 sm:p-3 font-semibold text-gray-700 hidden md:table-cell">Location</th>
                <th className="text-center p-2 sm:p-3 font-semibold text-gray-700">Claims</th>
                <th className="text-right p-2 sm:p-3 font-semibold text-gray-700">Total (USD)</th>
                <th className="text-right p-2 sm:p-3 font-semibold text-gray-700 hidden sm:table-cell">Avg (USD)</th>
                <th className="text-center p-2 sm:p-3 font-semibold text-gray-700">Pricelist</th>
              </tr>
            </thead>
            <tbody>
              {paginatedHospitals.map((hospital, idx) => (
                <tr key={hospital.name} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-2 sm:p-3 text-gray-600">{startIdx + idx + 1}</td>
                  <td className="p-2 sm:p-3 font-medium text-gray-800">{hospital.name}</td>
                  <td className="p-2 sm:p-3 text-gray-600 hidden md:table-cell">{hospital.location}</td>
                  <td className={`p-2 sm:p-3 text-center font-semibold ${hospital.count > 0 ? 'text-purple-600' : 'text-gray-400'}`}>
                    {hospital.count}
                  </td>
                  <td className={`p-2 sm:p-3 text-right font-semibold ${hospital.total > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                    ${hospital.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className={`p-2 sm:p-3 text-right font-semibold hidden sm:table-cell ${avgTone(hospital.avg)}`} data-testid={`hospital-avg-${hospital.name}`}>
                    ${hospital.avg.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="p-2 sm:p-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => printHospitalPricelist(hospital.name, authCfg)}
                        className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                        title="Print Pricelist"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => downloadHospitalPricelist(hospital.name, authCfg)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Download Pricelist (Excel/CSV)"
                      >
                        <FileSpreadsheet className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-emerald-50 border-t-2 border-emerald-200">
              <tr>
                <td colSpan="2" className="p-2 sm:p-3 font-bold text-emerald-800">Total</td>
                <td className="p-2 sm:p-3 hidden md:table-cell"></td>
                <td className="p-2 sm:p-3 text-center font-bold text-emerald-800">{totalClaims}</td>
                <td className="p-2 sm:p-3 text-right font-bold text-emerald-800">
                  ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td className={`p-2 sm:p-3 text-right font-bold hidden sm:table-cell ${avgClaim > 0 ? avgTone(avgClaim) : 'text-emerald-800'}`} data-testid="hospital-avg-total">
                  ${avgClaim.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td className="p-2 sm:p-3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((prev) => Math.max(0, prev - 1))}
              disabled={page === 0}
              className="h-8 w-8 p-0"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-gray-600 min-w-[80px] text-center">
              Page {page + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
              disabled={page >= totalPages - 1}
              className="h-8 w-8 p-0"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
